import React, { useContext, useEffect, useState } from 'react';

import { X } from 'lucide-react';
import { Link } from 'react-router';

import AJAX from 'common/AJAX';
import { AutopilotImportErrorMessages } from 'common/constants/autopilotImport';
import { SyncIntegrationNames } from 'common/constants/autopilotIntegrations';
import { type LocalFile, MimeTypes } from 'common/constants/files';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import ErrorMessage from 'common/ErrorMessage';
import FileRenderer from 'common/file/FileRenderer';
import { parseToLocalFile } from 'common/file/utils/parseToLocalFile';
import Form from 'common/Form';
import useMutation from 'common/hooks/useMutation';
import FileInput from 'common/inputs/FileInput';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UserCompanySelect from 'common/subdomain/admin/UserCompanySelect';
import Flex from 'common/ui/Flex';
import HStack from 'common/ui/HStack';
import Popover, { type PopoverContentProps } from 'common/ui/Popover';
import VStack from 'common/ui/VStack';
import Button from 'common/uiV3/Button';
import Field from 'common/uiV3/Field';
import Input from 'common/uiV3/Input';
import Textarea from 'common/uiV3/Textarea';

import CircleCheck from 'img/icons/circle-check.svg';

import type { AutopilotImportData } from 'common/api/endpoints/enqueue';
import type { SuccessResponse } from 'common/api/responses';

const AcceptedMimeTypes = [...MimeTypes.csv];

type AdminAutopilotImportPopoverProps = Pick<
  PopoverContentProps,
  'side' | 'align' | 'sideOffset'
> & {
  trigger: React.ReactNode;
};

type LocalFileWithOriginal = LocalFile & {
  originalFile: File;
};

const AdminAutopilotImportPopover = ({
  trigger,
  side,
  align,
  sideOffset,
}: AdminAutopilotImportPopoverProps) => {
  const showToast = useContext(ShowToastContext);
  const location = useContext(LocationContext);
  const router = useContext(RouterContext);
  const company = useContext(CompanyContext);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [userID, setUserID] = useState<string>();
  const [thirdPartyCompanyID, setThirdPartyCompanyID] = useState<string>();
  const [text, setText] = useState<string>();
  const [sourceURL, setSourceURL] = useState<string>();

  const [files, setFiles] = useState<LocalFileWithOriginal[]>([]);

  const isIDBEnabled = company.featureAllowlist.includes('idea-database');

  const hasFiles = files.length > 0;
  const hasAttribution = isIDBEnabled ? userID || thirdPartyCompanyID : userID;
  const canSubmit = (hasAttribution && text) || hasFiles;

  useEffect(() => {
    if (location.query.autopilotImport === 'true') {
      setIsOpen(true);
    }
  }, [location.query.autopilotImport]);

  const sendAutopilot = useMutation<SuccessResponse, AutopilotImportData>({
    mutationFn: async (input) => {
      if (input.files && input.files.length > 0) {
        return AJAX.postFiles('/api/queue/import', input.files);
      } else {
        return AJAX.post('/api/queue/import', input);
      }
    },
    errors: AutopilotImportErrorMessages,
    onSuccess: () => {
      setIsSuccess(true);
      showToast('Feedback sent to autopilot', ToastTypes.success);
      resetForm();
    },
    onError: () => showToast('Failed to send to autopilot', ToastTypes.error),
  });

  const resetForm = () => {
    setThirdPartyCompanyID(undefined);
    setUserID(undefined);
    setText(undefined);
    setSourceURL(undefined);
    setFiles([]);
  };

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }

    sendAutopilot.mutate({
      userID: userID ?? null,
      thirdPartyCompanyID: thirdPartyCompanyID ?? null,
      text: text ?? undefined,
      files: hasFiles ? files.map((f) => f.originalFile) : undefined,
      sourceURL: sourceURL ?? undefined,
    });
  };

  const handleFileUpload = (file: File) => {
    const localFile = parseToLocalFile(file);

    if (files.some((f) => f.name === localFile.name)) {
      return;
    }

    setFiles((prevFiles) => [
      ...prevFiles,
      {
        ...localFile,
        originalFile: file,
      },
    ]);
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (!open) {
      if (location.query.autopilotImport === 'true') {
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, autopilotImport: undefined },
        });
      }
    }
  };

  const handleFileRemoved = (file: LocalFile) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.name !== file.name));
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Content side={side} align={align} sideOffset={sideOffset} className="p-0 w-[450px]">
        <HStack className="base-border-b items-center justify-between px-4 py-2.5">
          <h3 className="text-lg font-semibold">Send to Autopilot</h3>
          <Popover.CloseButton asChild>
            <Button iconOnly aria-label="Close" size="small" variant="plain">
              <X />
            </Button>
          </Popover.CloseButton>
        </HStack>
        {isSuccess && (
          <AutopilotImportSuccess
            onSendMoreFeedback={() => setIsSuccess(false)}
            onSeeInbox={() => setIsOpen(false)}
          />
        )}
        {!isSuccess && (
          <>
            <VStack className="gap-3 p-4">
              <p className="text-base text-gray-110">
                Submit content you want Autopilot to review for feedback. Ideas and insights found
                will be created accordingly on behalf of your customers.
              </p>
              <Form
                acceptedFileTypes={AcceptedMimeTypes}
                allowFileUpload={true}
                dropZoneZIndex={101}
                onFile={handleFileUpload}>
                <VStack className="gap-3">
                  <Field>
                    <Field.Label>
                      <HStack className="items-center gap-1">
                        {hasFiles ? null : 'Details or'}
                        <FileInput
                          accept={AcceptedMimeTypes.join(',')}
                          defaultStyle={false}
                          onFile={handleFileUpload}
                          value={
                            <Button variant="unstyled" className="text-primary underline">
                              upload a .csv
                            </Button>
                          }
                        />
                      </HStack>
                    </Field.Label>
                    <Field.Content>
                      {hasFiles ? (
                        <FileRenderer files={files} onFileRemoved={handleFileRemoved} allowRemove />
                      ) : (
                        <Textarea
                          rows={4}
                          placeholder="Any text, meeting notes, emails, etc."
                          onChange={(e) => setText(e.target.value)}
                          autoFocus
                        />
                      )}
                    </Field.Content>
                  </Field>
                  {!hasFiles && (
                    <>
                      <Field>
                        <Field.Label>Who gave the feedback?</Field.Label>
                        <Field.Content>
                          <HStack className="gap-2">
                            <UserCompanySelect
                              onChange={({ userID, thirdPartyCompanyID }) => {
                                setUserID(userID);
                                setThirdPartyCompanyID(thirdPartyCompanyID);
                              }}
                            />
                          </HStack>
                        </Field.Content>
                      </Field>
                      <Field>
                        <Field.Label>Source</Field.Label>
                        <Field.Content>
                          <Input placeholder="Source URL" onChange={setSourceURL} />
                        </Field.Content>
                      </Field>
                    </>
                  )}
                </VStack>
              </Form>
              {sendAutopilot.error && <ErrorMessage>{sendAutopilot.error.message}</ErrorMessage>}
            </VStack>
            <HStack className="px-4 pb-4 justify-end gap-2">
              <Popover.CloseButton asChild>
                <Button variant="outlined">Cancel</Button>
              </Popover.CloseButton>
              <Button
                onClick={handleSubmit}
                isLoading={sendAutopilot.isLoading}
                disabled={!canSubmit}>
                Submit
              </Button>
            </HStack>
          </>
        )}
      </Popover.Content>
    </Popover>
  );
};

type AutopilotImportSuccessProps = {
  onSendMoreFeedback: () => void;
  onSeeInbox: () => void;
};

const AutopilotImportSuccess = ({
  onSendMoreFeedback,
  onSeeInbox,
}: AutopilotImportSuccessProps) => {
  return (
    <VStack className="gap-6 py-32">
      <Flex className="justify-center items-center">
        <LazyLoadedImage src={CircleCheck} alt="Success" className="size-12" />
      </Flex>
      <VStack className="text-center gap-1">
        <h3 className="text-base font-semibold">Feedback sent</h3>
        <p className="text-base text-balance text-gray-110">
          Any feedback found will show in your Autopilot tab.
        </p>
      </VStack>
      <HStack className="gap-2.5 justify-center">
        <Button variant="outlined" asChild>
          <Link to={`/admin/autopilot?source=${SyncIntegrationNames.imports}`} onClick={onSeeInbox}>
            See Autopilot
          </Link>
        </Button>
        <Button onClick={onSendMoreFeedback}>Send more feedback</Button>
      </HStack>
    </VStack>
  );
};

export default AdminAutopilotImportPopover;
